"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { CalendarDays, Clock, ShieldCheck } from "@/components/icons";
import { EyebrowBadge } from "@/components/ui/eyebrow-badge";
import { HeroGlow } from "@/components/ui";
import { BOOK_DEMO_URL } from "@/lib/constants";

const perks = [
  {
    icon: CalendarDays,
    label: "30-minute session",
    detail: "A focused walkthrough tailored to your use case.",
  },
  {
    icon: Clock,
    label: "Fast scheduling",
    detail: "Pick a time that works for you — no back-and-forth.",
  },
  {
    icon: ShieldCheck,
    label: "No commitment",
    detail: "Ask anything. Zero pressure, zero obligation.",
  },
];

export default function RequestADemoSection() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <main id="main-content" tabIndex={-1}>
      <section
        className="border-border relative overflow-y-clip border-b pt-28 pb-16 lg:pt-32 lg:pb-24"
        aria-labelledby="demo-heading"
      >
        <HeroGlow align="left" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:sticky lg:top-28 lg:col-span-5"
            >
              <EyebrowBadge>Book a Demo</EyebrowBadge>

              <h1
                id="demo-heading"
                className="text-foreground mb-4 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
              >
                See Proxidize in action
              </h1>

              <p className="text-muted-foreground light:text-foreground/80 text-lg leading-relaxed">
                Get a live walkthrough from our team. We&apos;ll show you how Proxidize handles your
                specific use case and help you choose the right plan.
              </p>

              <motion.ul
                initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="mt-8 flex flex-col gap-5"
              >
                {perks.map(({ icon: Icon, label, detail }) => (
                  <li key={label} className="flex items-start gap-3">
                    <div className="border-border bg-background mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border">
                      <Icon size={15} className="text-muted-foreground" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-foreground text-sm leading-snug font-semibold">{label}</p>
                      <p className="text-muted-foreground mt-0.5 text-sm leading-snug">{detail}</p>
                    </div>
                  </li>
                ))}
              </motion.ul>
            </motion.div>

            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-7"
              aria-label="Schedule a demo"
            >
              <iframe
                src={BOOK_DEMO_URL}
                title="Book a demo"
                className="border-border w-full rounded-xl border"
                style={{ minWidth: 320, minHeight: 760 }}
              />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
